import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Box,
  Chip,
  Alert,
  Avatar,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';

const fieldTypes = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'attachment', label: 'Attachment' },
];

const profileModes = [
  { value: 'casual', label: 'Casual Profile' },
  { value: 'formal', label: 'Formal Profile' },
  { value: 'both', label: 'Both Profiles' },
];

const DynamicFieldManagement = () => {
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProfileMode, setSelectedProfileMode] = useState('casual');
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    isRequired: false,
    isActive: true,
    order: 0,
    options: [],
    validation: {},
    placeholder: '',
    helpText: '',
    masterDataSource: '',
    icon: '',
    profileMode: 'casual',
  });
  const [options, setOptions] = useState([{ label: '', value: '' }]);
  const [icons, setIcons] = useState([]);
  const [iconDialogOpen, setIconDialogOpen] = useState(false);

  useEffect(() => {
    fetchFields();
    fetchIcons();
  }, [selectedProfileMode]);

  useEffect(() => {
    fetchFields();
    fetchIcons();
  }, []);

  const fetchIcons = async () => {
    try {
      const response = await axios.get('/icons?active=true');
      setIcons(response.data.data);
    } catch (error) {
      console.error('Failed to fetch icons:', error);
    }
  };

  const fetchFields = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/dynamic-fields?profileMode=${selectedProfileMode}`);
      setFields(response.data.data);
    } catch (error) {
      setError('Failed to fetch fields');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        fieldName: field.fieldName,
        fieldLabel: field.fieldLabel,
        fieldType: field.fieldType,
        isRequired: field.isRequired,
        isActive: field.isActive,
        order: field.order,
        options: field.options || [],
        validation: field.validation || {},
        placeholder: field.placeholder || '',
        helpText: field.helpText || '',
        masterDataSource: field.masterDataSource || '',
        icon: field.icon?._id || '',
        profileMode: field.profileMode || 'both',
      });
      setOptions(field.options?.length ? field.options : [{ label: '', value: '' }]);
    } else {
      setEditingField(null);
      setFormData({
        fieldName: '',
        fieldLabel: '',
        fieldType: 'text',
        isRequired: false,
        isActive: true,
        order: fields.length,
        options: [],
        validation: {},
        placeholder: '',
        helpText: '',
        masterDataSource: '',
        icon: '',
        profileMode: selectedProfileMode,
      });
      setOptions([{ label: '', value: '' }]);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingField(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = value;
    setOptions(newOptions);
  };

  const addOption = () => {
    setOptions([...options, { label: '', value: '' }]);
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const handleIconSelect = (iconId) => {
    setFormData(prev => ({ ...prev, icon: iconId }));
    setIconDialogOpen(false);
  };

  const renderIcon = (icon) => {
    if (icon.type === 'material-icon') {
      return (
        <Box 
          component="span" 
          className="material-icons" 
          sx={{ 
            fontSize: icon.size === 'small' ? 20 : icon.size === 'large' ? 40 : 30,
            color: icon.color 
          }}
        >
          {icon.iconCode}
        </Box>
      );
    } else {
      return (
        <Avatar 
          src={icon.imageUrl} 
          sx={{ 
            width: icon.size === 'small' ? 20 : icon.size === 'large' ? 40 : 30,
            height: icon.size === 'small' ? 20 : icon.size === 'large' ? 40 : 30,
          }}
        >
          <ImageIcon />
        </Avatar>
      );
    }
  };

  const getSelectedIcon = () => {
    return icons.find(icon => icon._id === formData.icon);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const submitData = {
        ...formData,
        options: (formData.fieldType === 'select' || formData.fieldType === 'multiselect') 
          ? options.filter(opt => opt.label && opt.value) 
          : [],
      };

      if (editingField) {
        await axios.put(`/dynamic-fields/${editingField._id}`, submitData);
      } else {
        await axios.post('/dynamic-fields', submitData);
      }

      await fetchFields();
      handleClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save field');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        await axios.delete(`/dynamic-fields/${id}`);
        await fetchFields();
      } catch (error) {
        setError('Failed to delete field');
      }
    }
  };

  const handleToggleActive = async (field) => {
    try {
      await axios.put(`/dynamic-fields/${field._id}`, {
        ...field,
        isActive: !field.isActive,
      });
      await fetchFields();
    } catch (error) {
      setError('Failed to update field status');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dynamic Profile Fields
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Field
        </Button>
      </Box>

      {/* Profile Mode Selector */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Profile Mode</InputLabel>
          <Select
            value={selectedProfileMode}
            onChange={(e) => setSelectedProfileMode(e.target.value)}
            label="Profile Mode"
          >
            {profileModes.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                {mode.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Field Name</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Profile Mode</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field) => (
              <TableRow key={field._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DragIcon sx={{ mr: 1, color: 'grey.500' }} />
                    {field.order}
                  </Box>
                </TableCell>
                <TableCell>{field.fieldName}</TableCell>
                <TableCell>{field.fieldLabel}</TableCell>
                <TableCell>
                  <Chip 
                    label={field.fieldType} 
                    variant="outlined" 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={profileModes.find(mode => mode.value === (field.profileMode || 'both'))?.label || (field.profileMode || 'Both Profiles')} 
                    color={(field.profileMode === 'both' || !field.profileMode) ? 'primary' : field.profileMode === 'formal' ? 'secondary' : 'default'}
                    variant="outlined" 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={field.isRequired ? 'Yes' : 'No'} 
                    color={field.isRequired ? 'primary' : 'default'}
                    variant="outlined" 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.isActive}
                        onChange={() => handleToggleActive(field)}
                        size="small"
                      />
                    }
                    label={field.isActive ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpen(field)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(field._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingField ? 'Edit Field' : 'Add New Field'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="fieldName"
                label="Field Name"
                value={formData.fieldName}
                onChange={handleInputChange}
                required
                disabled={!!editingField}
                helperText="Unique identifier for the field (cannot be changed after creation)"
              />
              
              <TextField
                name="fieldLabel"
                label="Field Label"
                value={formData.fieldLabel}
                onChange={handleInputChange}
                required
              />

              <FormControl required>
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="fieldType"
                  value={formData.fieldType}
                  onChange={handleInputChange}
                  label="Field Type"
                >
                  {fieldTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl required>
                <InputLabel>Profile Mode</InputLabel>
                <Select
                  name="profileMode"
                  value={formData.profileMode}
                  onChange={handleInputChange}
                  label="Profile Mode"
                >
                  {profileModes.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                name="placeholder"
                label="Placeholder"
                value={formData.placeholder}
                onChange={handleInputChange}
              />

              <TextField
                name="helpText"
                label="Help Text"
                value={formData.helpText}
                onChange={handleInputChange}
                multiline
                rows={2}
              />

              <TextField
                name="order"
                label="Display Order"
                type="number"
                value={formData.order}
                onChange={handleInputChange}
                required
              />

              {/* Icon Selection */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Field Icon (Optional)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getSelectedIcon() ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid grey', borderRadius: 1 }}>
                      {renderIcon(getSelectedIcon())}
                      <Typography variant="body2">{getSelectedIcon().displayName}</Typography>
                      <IconButton size="small" onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No icon selected
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => setIconDialogOpen(true)}
                    startIcon={<ImageIcon />}
                  >
                    {getSelectedIcon() ? 'Change Icon' : 'Select Icon'}
                  </Button>
                </Box>
              </Box>

              {(formData.fieldType === 'select' || formData.fieldType === 'multiselect') && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Options
                  </Typography>
                  {options.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        label="Label"
                        value={option.label}
                        onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                        size="small"
                      />
                      <TextField
                        label="Value"
                        value={option.value}
                        onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                        size="small"
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeOption(index)}
                        disabled={options.length === 1}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Button variant="outlined" onClick={addOption} sx={{ mt: 1 }}>
                    Add Option
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isRequired"
                      checked={formData.isRequired}
                      onChange={handleInputChange}
                    />
                  }
                  label="Required Field"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Icon Selection Dialog */}
      <Dialog open={iconDialogOpen} onClose={() => setIconDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Select Icon
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {icons.map((icon) => (
                <Grid item xs={6} sm={4} md={3} key={icon._id}>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: formData.icon === icon._id ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      cursor: 'pointer',
                      textAlign: 'center',
                      bgcolor: formData.icon === icon._id ? 'primary.50' : 'transparent',
                      '&:hover': {
                        bgcolor: formData.icon === icon._id ? 'primary.100' : 'grey.50',
                      }
                    }}
                    onClick={() => handleIconSelect(icon._id)}
                  >
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                      {renderIcon(icon)}
                    </Box>
                    <Typography variant="caption" display="block">
                      {icon.displayName}
                    </Typography>
                    <Chip 
                      label={icon.category} 
                      size="small" 
                      variant="outlined"
                      sx={{ mt: 0.5, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
            {icons.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                No icons available. Please add some icons first.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIconDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleIconSelect('')}
            color="secondary"
          >
            Clear Selection
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DynamicFieldManagement;